import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Confetti, ConfettiRef } from '@/components/ui/confetti';
import { useActivationCompletion } from '@/hooks/use-activation-completion';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { APP_ROUTE } from '@/constants';
import { QuickStartActiveStepState, useDestinations } from '@/states';
import { ClientContainer, UserContainer } from '@/providers';
import { trackEvent } from '@/helpers/external-tools-helper';
import { ACTIVATION_ANALYTICS_EVENTS } from '@/analytics/activation';
import useTemplates from '@/hooks/use-templates';
import { toast } from '@/hooks/use-toast';
import { useDemoMode } from '@/hooks/use-demo-mode';
import { useSwitchBusiness } from '@/hooks/use-business-switch';
import { BusinessSwitchLoader } from '@/components/ui/business-switch-loader';

import { businessSwitchAtom } from '@/states/business-switch';

interface ActionItemProps {
  label: string;
  isCompleted?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  description?: string;
}

const ActionItem: React.FC<ActionItemProps> = ({
  label,
  isCompleted,
  isActive,
  onClick,
  description,
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-2 py-1 px-1.5 rounded-md transition-colors text-xs font-medium w-full',
            isCompleted
              ? 'text-gray-400 cursor-default'
              : 'text-gray-800 cursor-pointer hover:bg-gray-50',
            isActive ? 'bg-gray-50' : ''
          )}
          onClick={isCompleted ? undefined : onClick}
          disabled={isCompleted}
        >
          <span className="inline-flex items-center justify-center w-4 h-4">
            <i
              className={`ti ${
                isCompleted
                  ? 'ti-circle-check text-green-400'
                  : 'ti-circle-check text-gray-400'
              } text-[13px]`}
            />
          </span>
          <span
            className={cn('truncate text-left', isCompleted && 'line-through')}
          >
            {label}
          </span>
        </button>
      </TooltipTrigger>
      {description && !isCompleted && (
        <TooltipContent side="right" className="max-w-[200px]">
          <p className="text-xs">{description}</p>
        </TooltipContent>
      )}
    </Tooltip>
  </TooltipProvider>
);

const ActivationFlowSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('activation');

  // State
  const [activeStep, setActiveStep] = useRecoilState(QuickStartActiveStepState);
  const { quickStart } = useActivationCompletion(); // Use centralized hook
  const [showDemoModal, setShowDemoModal] = useState(false);
  const businessSwitchState = useAtomValue(businessSwitchAtom);
  const isLoadingDemo = businessSwitchState.isSwitching;

  // Confetti state
  const confettiRef = useRef<ConfettiRef>(null);
  const [previousQuickStart, setPreviousQuickStart] = useState(quickStart);

  // Containers
  const { state: clientState } = ClientContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();

  // Hooks
  const { getDestinations } = useDestinations();
  const { getTemplates } = useTemplates();
  const { isDemoMode } = useDemoMode();
  const switchBusiness = useSwitchBusiness();

  const completedSteps = Object.values(quickStart).filter(
    step => step === 'completed'
  ).length;
  const totalSteps = Object.keys(quickStart).length;
  const progress = Math.round((completedSteps / totalSteps) * 100);

  useEffect(() => {
    getDestinations();
    getTemplates();
  }, []);

  const triggerSideCannons = useCallback(() => {
    const colors = [
      '#FF3D7F',
      '#FF9A3D',
      '#FFE23D',
      '#3DFF7F',
      '#3DFFF5',
      '#3D7FFF',
      '#9A3DFF',
      '#FF3DD1',
      '#3DFF9A',
      '#FF573D',
    ];
    // This setTimeout is a workaround to fix the issue with confetti not being triggered programmatically
    setTimeout(() => {
      confettiRef.current?.fire({
        particleCount: 30,
        angle: 60,
        spread: 35,
        startVelocity: 30,
        origin: { x: 0, y: 1 },
        colors,
        scalar: 0.6,
      });

      confettiRef.current?.fire({
        particleCount: 30,
        angle: 120,
        spread: 35,
        startVelocity: 30,
        origin: { x: 1, y: 1 },
        colors,
        scalar: 0.6,
      });
    }, 100);
  }, [confettiRef]);

  useEffect(() => {
    const stepKeys = Object.keys(quickStart) as Array<keyof typeof quickStart>;
    const hasNewCompletion = stepKeys.some(key => {
      const previousValue = previousQuickStart[key];
      const currentValue = quickStart[key];

      return currentValue === 'completed' && previousValue !== 'completed';
    });

    if (hasNewCompletion) {
      triggerSideCannons();
    }

    setPreviousQuickStart(quickStart);
  }, [previousQuickStart, quickStart, triggerSideCannons]);

  const navigateToStep = useCallback(
    (step: keyof typeof quickStart) => {
      const pathMap = {
        account: `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.DASHBOARD}`,
        datasources: `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DATA_SOURCES}/${APP_ROUTE.ADD}`,
        destination: `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DESTINATION}`,
        report: `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.REPORTING}/${APP_ROUTE.REPORTS}`,
      };

      const path = pathMap[step];
      if (path) {
        navigate(path);
      }
    },
    [navigate, clientState.selectedClient?.id]
  );

  const handleNavigateToDataSources = () => {
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_ADD_DATASOURCE_STARTED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );
    navigateToStep('datasources');
  };

  const handleNavigateToDestination = () => {
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_ADD_DESTINATION_STARTED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );
    navigateToStep('destination');
  };

  const handleNavigateToReports = () => {
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_CREATE_REPORT_STARTED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );
    navigateToStep('report');
  };

  const handleStepClick = (stepKey: keyof typeof quickStart) => {
    if (quickStart[stepKey] === 'completed') return;

    setActiveStep(stepKey as any);

    if (stepKey === 'datasources') {
      handleNavigateToDataSources();
    } else if (stepKey === 'destination') {
      handleNavigateToDestination();
    } else if (stepKey === 'report') {
      handleNavigateToReports();
    }
  };

  const handleSwitchToDemo = useCallback(async () => {
    try {
      setShowDemoModal(false);

      // Find demo business from businessOptions
      const demoBusiness = userState.businessOptions?.find(
        (business: any) => business.is_demo_account === true
      );

      if (!demoBusiness) {
        toast({
          variant: 'destructive',
          title: t('sidebar_demo_not_found', 'Demo business not found'),
          description: t(
            'sidebar_demo_not_found_description',
            'Unable to find the demo business account.'
          ),
        });
        return;
      }

      // Use the same business switching logic as demo mode banner and sidebar card
      // The hook will manage the global loading state
      await switchBusiness({
        business: demoBusiness,
        currentPath: location.pathname,
        currentClientId: clientState?.selectedClient?.id,
      });

      // Component will unmount during business switch
      // Global loading state is managed by the hook
    } catch (error) {
      console.error('Error switching to demo mode:', error);
      toast({
        variant: 'destructive',
        title: t('sidebar_demo_switch_error', 'Unable to switch to demo mode'),
        description: t(
          'sidebar_demo_switch_error_description',
          'Please try again.'
        ),
      });
    }
  }, [
    userState.businessOptions,
    clientState?.selectedClient?.id,
    switchBusiness,
    location.pathname,
    t,
  ]);

  return (
    <>
      {isLoadingDemo && (
        <BusinessSwitchLoader message={businessSwitchState.message} />
      )}
      <div className="relative overflow-hidden">
        <div className="w-full bg-white rounded-lg border border-gray-100 p-3">
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-gray-900">
                  {t('sidebar_getting_started', 'Getting Started')}
                </span>
                <span className="text-[11px] font-medium text-gray-500">
                  {progress}%
                </span>
              </div>
              <div className="h-1 bg-gray-100 rounded-full overflow-hidden mb-1">
                <div
                  className="h-full bg-green-500 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                  role="progressbar"
                  aria-label="Setup completion progress"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>
            {/* Steps */}
            <div className="space-y-0.5">
              <ActionItem
                label={t('sidebar_setup_account', 'Setup account')}
                isCompleted={quickStart.account === 'completed'}
                isActive={activeStep === 'account'}
                onClick={() => handleStepClick('account')}
                description={t(
                  'sidebar_setup_account_description',
                  'Complete your account profile and business information'
                )}
              />
              <ActionItem
                label={t('sidebar_add_data_sources', 'Add data sources')}
                isCompleted={quickStart.datasources === 'completed'}
                isActive={activeStep === 'datasources'}
                onClick={() => handleStepClick('datasources')}
                description={t(
                  'sidebar_add_data_sources_description',
                  'Connect your marketing platforms (social media, ads, email, website)'
                )}
              />
              <ActionItem
                label={t(
                  'sidebar_connect_google_drive',
                  'Connect Google Drive'
                )}
                isCompleted={quickStart.destination === 'completed'}
                isActive={activeStep === 'destination'}
                onClick={() => handleStepClick('destination')}
                description={t(
                  'sidebar_connect_google_drive_description',
                  'Connect Google Drive to save and export reports as Google Slides/Sheets'
                )}
              />
              <ActionItem
                label={t('sidebar_create_report', 'Create report')}
                isCompleted={quickStart.report === 'completed'}
                isActive={activeStep === 'report'}
                onClick={() => handleStepClick('report')}
                description={t(
                  'sidebar_create_report_description',
                  'Create your first performance report with your connected data'
                )}
              />
            </div>
            {/* Action Buttons Row */}
            <div className="space-y-2 pt-2 border-t border-gray-100">
              {!isDemoMode && (
                <button
                  id="try-demo-mode-button"
                  className="relative w-full flex items-center gap-2 rounded-lg px-3 py-2 transition-all duration-200 group border border-gray-200 hover:border-gray-300 bg-gradient-to-br from-white to-gray-50/30 hover:shadow-md hover:scale-[1.01]"
                  onClick={() => setShowDemoModal(true)}
                >
                  {/* Icon with gradient background */}
                  <div className="relative flex-shrink-0 w-6 h-6 rounded-md bg-gradient-to-br from-pink-500 via-rose-500 to-orange-500 flex items-center justify-center overflow-hidden">
                    <span
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                      style={{
                        animation: 'shimmer 2.5s ease-in-out infinite',
                        transform: 'skewX(-20deg)',
                      }}
                    />
                    <i className="ti ti-sparkles text-xs text-white relative z-10" />
                  </div>

                  {/* Text content */}
                  <div className="flex-1 text-left">
                    <div className="text-xs font-semibold text-gray-900">
                      {t('sidebar_try_demo_mode', 'Try Demo Mode')}
                    </div>
                  </div>

                  {/* Arrow icon */}
                  <i className="ti ti-chevron-right text-sm text-gray-400 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </button>
              )}

              <button
                className="w-full text-xs text-primary hover:text-primary/90 font-semibold hover:bg-primary/5 rounded-md px-3 py-2 transition-colors"
                onClick={() => {
                  const firstIncompleteStep = Object.entries(quickStart).find(
                    ([_, status]) => status !== 'completed'
                  )?.[0] as keyof typeof quickStart;

                  if (firstIncompleteStep) {
                    navigateToStep(firstIncompleteStep);
                    trackEvent(
                      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_STARTED,
                      {
                        businessId: userState.business?.id,
                        clientId: clientState.selectedClient?.id,
                      }
                    );
                  }
                }}
              >
                {t('sidebar_complete_setup', 'Complete setup')}
              </button>
            </div>

            {/* Keyframe animations */}
            <style>
              {`
            @keyframes glare {
              0%, 100% {
                transform: translateX(-100%) skewX(-12deg);
              }
              50% {
                transform: translateX(200%) skewX(-12deg);
              }
            }
            @keyframes border-glare {
              0% {
                background-position: 200% 0;
              }
              100% {
                background-position: -200% 0;
              }
            }
            @keyframes shimmer {
              0% {
                transform: translateX(-150%) skewX(-20deg);
              }
              50% {
                transform: translateX(150%) skewX(-20deg);
              }
              100% {
                transform: translateX(150%) skewX(-20deg);
              }
            }
          `}
            </style>
          </div>
        </div>
        <Confetti
          ref={confettiRef}
          className="absolute inset-0 top-auto pointer-events-none"
          manualstart
        />

        {/* Demo Mode Confirmation Modal */}
        <Dialog open={showDemoModal} onOpenChange={setShowDemoModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
                  <i className="ti ti-sparkles text-xl text-primary" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-lg">
                    {t('sidebar_demo_modal_title', 'Switch to Demo Mode?')}
                  </DialogTitle>
                  <DialogDescription className="text-sm mt-1">
                    {t(
                      'sidebar_demo_modal_subtitle',
                      'Explore with sample data'
                    )}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-3 py-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t(
                  'sidebar_demo_modal_description',
                  "You'll be switched to a demo business with pre-populated sample data. This lets you explore the platform's features without connecting your own accounts."
                )}
              </p>

              <div className="rounded-lg bg-muted/30 p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <i className="ti ti-info-circle text-sm text-primary mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'sidebar_demo_modal_info',
                      'You can switch back to your primary business anytime from the account switcher from Settings > Account Settings.'
                    )}
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button
                variant="ghost"
                onClick={() => setShowDemoModal(false)}
                disabled={isLoadingDemo}
                className="w-full sm:w-auto"
              >
                {t('sidebar_demo_modal_cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleSwitchToDemo}
                disabled={isLoadingDemo}
                className="w-full sm:w-auto gap-2"
              >
                {isLoadingDemo ? (
                  <>
                    <Spinner size="sm" />
                    <span>
                      {t('sidebar_demo_modal_switching', 'Switching...')}
                    </span>
                  </>
                ) : (
                  <>
                    <i className="ti ti-arrow-right" />
                    <span>
                      {t('sidebar_demo_modal_switch', 'Switch to Demo')}
                    </span>
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default ActivationFlowSidebar;
